/**
 * 出片请求组包 —— **提交与 prompt 预览共用的唯一一份**(2026-08-06)。
 *
 * ═══ 为什么要抽出来 ═══════════════════════════════════════════════════════════
 * 幕二右栏要显示「我们真正发给视频模型的那条 prompt」。那条 prompt 由 ai 侧编译层从
 * 这份请求体算出来 —— 所以**预览发的必须与出片发的是同一份**。若预览这条路自己另组一套
 * (哪怕只少一个 `shotSizes`),右栏显示的就不是会真发出去的那条,而用户会照着它调分镜。
 * 那比不给预览更糟,且不会有任何报错提示这件事。
 *
 * 同样的口径在另外两仓也各钉了一遍:
 *   - api `VideoService#compilePreview` 复用 `VideoPayloadMapper.toRequestMap`(与真派发同一个 mapper);
 *   - ai `preview_chunk_prompts` 复用出片路的 `shots_from_request` / `chunk_shots` / `compile_chunk_prompt`。
 * 三层各自"同源",链路才真的不撒谎。
 *
 * ★本函数是**纯函数**:只读入参、不碰 state、不发请求 —— 这样两个调用方拿到的必然逐字相同。
 */
import { BARE_SEND_FALLBACK_MAX_SEC, bareSendDurationSec } from "./effectiveDuration";
import { effectiveTaskPurpose, intentProvenanceRef } from "./purposeNotice";
import { entitiesWithCharacter, withIdentityReferences } from "./characterPick";
import { freeMaterialRequest } from "./freeMaterial";
import type { FormState } from "./videoCreateTypes";
import { isToneRegister } from "./toneRegister";
import type { VideoTaskCreateRequest } from "../api/videoService";

/**
 * 每镜环境 / 动作的字数上限 —— 与 ai `app/modules/video/api/routes.py` 的
 * 「每镜环境 ≤24 字中文 / 动作 ≤36 字」同源(0824 丰满化批一起放宽:编译预算已按镜数弹)。
 * 改这里之前先改那边(两边不一致时以 ai 侧为准:它是真正拿去编 prompt 的那一端)。
 */
/**
 * ★0826 质感批 ⑧:24/36 → 60/90(用户报「可填写文字的地方限制太少,无法具体描述画面」)。
 * ★VID-RICH-1(2026-09-01 用户拍板加厚):真单实测实发只用引擎预算 ~10%(532/5507),
 *   「过少的文案导致场景控制变弱」。ai 侧三层钳同批抬(窄口 90/64、段预算 1000+300/镜、
 *   负向折入 2300),本侧对齐:环境 60→64(此前两侧登记已漂 —— ai 还是 44/30,用户在
 *   分镜台多写的字一直被 ai 入站钳**静默截断**,本批钉回同数);预算镜像同步。
 * 天花板意识仍在:引擎预算 5507,6 镜/段 1900 ≈ 1/3,防的是堆形容词把注意力稀释回去。
 */
/**
 * 每镜可用的编译字数 —— 复刻 ai `shot_prompt_compiler.max_prompt_chars`
 * (≤3 镜 1000 字,每多一镜 +300)再摊到每镜。用于校验输入框上限没有超出模型注意力预算。
 */
export function perShotCompileBudget(shotCount: number): number {
  const n = Math.max(1, shotCount);
  const total = 1000 + 300 * Math.max(0, n - 3);
  return total / n;
}

export const PANEL_SCENE_MAX = 64;
export const PANEL_MOTION_MAX = 90;

/** 逐条钳长度;undefined/空数组原样返回(不制造空数组改变 isMultiShot 判定)。 */
function clampEach(arr: string[] | null | undefined, max: number): string[] | undefined {
  if (!arr) return undefined;
  return arr.map((s) => (typeof s === "string" && s.length > max ? s.slice(0, max) : s));
}

export interface BuildTaskRequestInput {
  form: FormState;
  /** 约束派生后的真实 provider/model(useVideoProviderConstraints 产)。 */
  effectiveModel: { provider: string; model: string };
  effectiveMode: string;
  /** B6 场景打标(只有从目的卡来的才有);预览不需要,但同源起见一并接。 */
  sceneKey?: string | null;
  currentDraftId?: number | null;
  /**
   * 直发单镜时长上限(0824:跟模型走,2.5=30s)。页面从 selectedCap.durationRange 取
   * (bareSendMaxSec);缺省保守 15 —— 组包是纯函数,能力表由调用方注入。
   */
  bareSendMaxSec?: number;
  /**
   * 批 L:正式报价单号。**预览路径不传**(预览不落单,也不该落)——
   * 只有真提交那一次才带,组包函数原样透传,不在这里生成也不在这里判空。
   */
  quoteId?: string | null;
}

export function buildTaskRequest({
  form,
  effectiveModel,
  effectiveMode,
  sceneKey,
  currentDraftId,
  bareSendMaxSec: bareMax = BARE_SEND_FALLBACK_MAX_SEC,
  quoteId,
}: BuildTaskRequestInput): VideoTaskCreateRequest {
  const isMultiShot = !!(form.panelScenes && form.panelScenes.length >= 2);
  // 多格 userInput = LLM 主题(给 backend 做 task name fallback;ai-service 用的是 panelScenes)
  const effectiveUserInput = isMultiShot
    ? form.userInput.trim() || form.theme || `${form.panelScenes!.length} 镜分镜视频`
    : form.userInput.trim();
  // M2 成片档案(P0-2 §5-6):蓝图溯源随 sourceRef 落任务(蓝图=来源的一种;requestPayload
  // 原样存 → 详情页展示「本片套用:XXX」),复用既有自由 Map 通道零 DTO 改动。
  const blueprintRef = form.explosiveFormulaName
    ? {
        explosiveBlueprintId: form.explosiveBlueprintId ?? undefined,
        explosiveFormulaName: form.explosiveFormulaName,
        explosiveFidelityMode: form.explosiveFidelityMode ?? "learn",
        // 2026-08-14「参考片在拍什么」:走 sourceRef 这条自由 Map 通道随任务上送。
        // ★与 userInput **分开的两个字段** —— 引擎那边一个是「照着拍什么」、一个是
        //   「用户的产品事实」。并进 userInput 会被判成用户卖点,成片就讲参考片的产品。
        ...(form.explosiveReferenceContent?.trim()
          ? { explosiveReferenceContent: form.explosiveReferenceContent.trim() }
          : {}),
      }
    : {};
  /**
   * 波2 成片档案:这条片的拍法随 sourceRef 落任务(自由 Map 通道,零 DTO 改动;
   * 与蓝图溯源同一个范式)→ requestPayload 原样存 → 详情页「本次设置」回显。
   *
   * ★两个字段各说各的、不合并:`formulaName` 是**引擎实际用了哪张**(compose 响应经
   * backend mergeStoryboard 写回 formJson),`pickedFormulaName` 是**用户选了哪张**。
   * 用户选了而引擎没套用(贴的是现成分镜脚本 → 忠实拆解不套拍法)时,前者为空、后者有值,
   * 详情页据此如实显示「未套用」—— 而不是把用户的选择当成结果显示(概览比实际乐观)。
   */
  const formulaRef = {
    ...(form.formulaKey ? { formulaKey: form.formulaKey } : {}),
    ...(form.formulaName ? { formulaName: form.formulaName } : {}),
    ...(form.pickedFormulaKey ? { pickedFormulaKey: form.pickedFormulaKey } : {}),
    ...(form.pickedFormulaName ? { pickedFormulaName: form.pickedFormulaName } : {}),
  };
  // 带上来源草稿 id:出片真正成功(SUCCESS)后由后端 complete 回调软删该草稿(失败/部分失败保留可重试)。
  // ★三份加料(草稿 id / 蓝图溯源 / 拍法档案)合并成一次判定:原来是嵌套三元,加第三份就得再套
  // 一层、且漏掉「只有拍法没有草稿」那格(裸发路)。全空时仍原样透传 form.sourceRef(行为不变)。
  const refExtras = {
    ...blueprintRef,
    ...formulaRef,
    // 意图出处随任务落(sourceRef 自由 Map 通道;为什么要落见 intentProvenanceRef docstring)
    ...intentProvenanceRef(form),
    ...(currentDraftId ? { draftId: currentDraftId } : {}),
  };

  const references = withIdentityReferences(form) ?? undefined;
  return {
    ...(sceneKey ? { sceneKey } : {}), // B6 场景打标
    // 批 L:正式报价单号(预览路径不传 → 不出现在预览请求体里,与「预览不落单」一致)
    ...(quoteId ? { quoteId } : {}),
    type: "PROMO",
    source: form.source,
    sourceRef:
      Object.keys(refExtras).length > 0
        ? { ...(form.sourceRef ?? {}), ...refExtras }
        : form.sourceRef,
    userInput: effectiveUserInput,
    name: form.name?.trim() || undefined,
    styleCode: form.styleCode,
    // 用户选的风格(预设 tone 或自定义文本)随请求落库,供详情/列表回显。
    briefStyle: form.briefStyle ?? undefined,
    industryCode: form.industryCode,
    provider: effectiveModel.provider as "seedance" | "happyhorse" | "wan2.7",
    model: effectiveModel.model,
    generationMode: effectiveMode as VideoTaskCreateRequest["generationMode"],
    resolution: form.resolution,
    ratio: form.ratio,
    // 多镜:请求级 durationSec 只是每镜 fallback(真·每镜时长在 panelDurations),必须 ≤15;
    // 不能把各镜之和(可能 24)塞进来撞后端 @Max(15)。取首镜时长(钳 2-15)。
    //
    // ★单镜(裸发)2026-08-11 修(测试反馈 E-14a,爱民 8·10 实测「选的30秒,出来的只有5秒」):
    //   这里原来是 `Math.min(15, Math.max(2, form.durationSec))` —— **完全不看时长档**。
    //   时长档(短15/中30/长45)存在 `form.briefDuration`,而 `form.durationSec` 是高级选项里的
    //   「单镜时长」,默认 5。于是用户在页面上选了「中·30s」、右上徽标也显示 30s,
    //   一走「不编排,直接出片」出来就是 5 秒 —— 页面说 30、发出去的是 5。
    //
    //   `effectiveDurationSec()` 早就定了口径(选了档 → 用档位秒数),徽标和积分都照它算,
    //   唯独**真正发出去的这一处**没照做 —— 又一次「同一个概念两个算法」(bug 9 的同款)。
    //   现在改成同源:用 effectiveDurationSec 再钳进模型单镜上限 15s。
    //
    // ★钳进模型单镜上限而不是原样发:单镜直发就是一次 provider 调用,超上限只会被
    //   provider 400。0824 起上限跟模型走(bareSendMaxSec:2.5=30s,2.0=15s,缺省保守 15)
    //   —— 此前写死 15,用户在 2.5 上直发 30s 被无谓钳短(用户实测点名)。仍超上限的档
    //   (2.0 选 30/45,2.5 选 45)由 VideoCreatePage 按钮旁提示 + 直发确认弹窗**明说**。
    //   钳位与告知缺一不可:只钳不说,用户下次还会以为选了 45 就是 45。
    durationSec: isMultiShot
      ? Math.min(15, Math.max(2, form.panelDurations?.[0] ?? 5))
      : bareSendDurationSec(form, bareMax),
    generateAudio: form.generateAudio,
    firstFrameUrl: form.firstFrameUrl,
    lastFrameUrl: form.lastFrameUrl,
    negativePrompt: form.negativePrompt,
    bgmId: form.bgmId ?? undefined,
    customBgmObjectKey: form.customBgmObjectKey ?? undefined,
    // 契约 v2 两个窄口:环境(基准数组,长度定镜数)+ 动作。ai 编译层据此单点出 prompt。
    // ★长度钳在这一处(2026-08-07 走查):ai routes.py 的「环境 ≤24 字 / 动作 ≤36」
    //   只是注释,不是 pydantic 校验 —— 超长不报错,而是悄悄把这一段推过 500 字预算,
    //   编译层随后按反 role 优先级截掉参考派活行(用户看不出哪里少了)。
    //   输入框的 maxLength 只拦手打,拦不住存量草稿里已有的超长值,也拦不住程序回写。
    panelScenes: clampEach(form.panelScenes, PANEL_SCENE_MAX),
    panelMotions: clampEach(form.panelMotions, PANEL_MOTION_MAX),
    panelSubjectRefs: form.panelSubjectRefs ?? undefined,
    // 每镜首帧:送已签名 url(Seedance 直接拉);bare key 给 i2i 锚点用,不送 provider
    panelFirstFrameUrls: form.panelFirstFrameUrls ?? undefined,
    // ★key 也一起送(2026-07-16):上面的 url 是会过期的签名,而后端 retry 是把
    // request_payload 原样重放的 —— 只送 url,隔天重试就是派一串死链。送了 key,
    // 后端 retry 时按 key 现签(VideoService#resolveFrameRefsForDispatch)。
    panelFirstFrameKeys: form.panelFirstFrameKeys ?? undefined,
    // 每镜生成模式:ai-service 据此逐镜分流 T2V/I2V(为空则按帧存在性回退)
    shotMode: form.shotMode ?? undefined,
    theme: form.theme ?? undefined,
    panelDurations: form.panelDurations ?? undefined,
    negativePrompts: form.negativePrompts ?? undefined,
    voiceovers: form.voiceovers ?? undefined,
    voiceTone: form.voiceTone ?? undefined,
    voiceEmotion: form.voiceEmotion ?? undefined,
    voiceEmotions: form.voiceEmotions ?? undefined,
    brandFocus: form.brandFocus ?? undefined,
    fontStyle: form.fontStyle ?? undefined,
    voiceSpeechRate: form.voiceSpeechRate ?? undefined,
    voiceVolume: form.voiceVolume ?? undefined,
    transition: form.transition ?? undefined,
    subtitleFontScale: form.subtitleFontScale ?? undefined,
    subtitlePosition: form.subtitlePosition ?? undefined,
    // ★effective 目的(2026-08-02):用户选的赢,没选用 AI 判的(resolvedPurpose)。
    // 此前只发 briefPurpose → 全库 0/23 没人选 = 任务 purpose 恒空:渲染期
    // derive_grade_look 兜底「产品带货」调色、详情/audit 目的全(空)。
    // 「是谁定的」在 sourceRef.purposeSource,不混(AI 推断不许装成用户选择)。
    purpose: effectiveTaskPurpose(form),
    // 调性档(0924 车 C):form 里存的就是生效档(默认值由创建页按行业写入);没有 = 不发,ai 按行业推。
    // ★B2 #15(0927 K4):自由创作**直发**(单镜、没选目的)不发 —— 创建页按租户行业写入的默认档(珠宝→luxury)
    //   会随直发单落库,详情页凭空显示「调性: 高奢静默」,而这页根本没有调性控件、直发路 ai 也不吃它。
    //   目的流 / 已编排多镜照发。
    ...(isToneRegister(form.toneRegister) && !(!isMultiShot && !form.briefPurpose)
      ? { toneRegister: form.toneRegister }
      : {}),
    // 结构化运镜/景别/布光/叙事透传(受控枚举;ai 编译器查表出中文短语 + 门控读)。
    // cameraSpeeds 与 cameraMovements 拆开传 —— 合并即「堆两个运镜」,官方 don't。
    shotSizes: form.shotSizes ?? undefined,
    cameraMovements: form.cameraMovements ?? undefined,
    cameraSpeeds: form.cameraSpeeds ?? undefined,
    lightingKeys: form.lightingKeys ?? undefined,
    shotIntents: form.shotIntents ?? undefined,
    narrativeRoles: form.narrativeRoles ?? undefined,
    heroMoments: form.heroMoments ?? undefined,
    // 批1 连续性透传(承接门在 ai render 时读;缺=老行为无条件承接)
    sceneIds: form.sceneIds ?? undefined,
    shotContinuities: form.shotContinuities ?? undefined,
    subjects: form.subjects ?? undefined,
    hookPlan: form.hookPlan ?? undefined,
    // item1 事实→画面:hero 镜 ai 编译期用 factVisual 取代 subjectMotion(缺=老行为)。
    factRefs: form.factRefs ?? undefined,
    factVisuals: form.factVisuals ?? undefined,
    // T2A 上传图实体化:模式路由器按图背书产品实体选 i2i-I2V(缺=0 图=今天的路)。
    // W0 事故修(任务 4853229421568):选人实体注入——渲染端 R2V 身份锁的数据源。
    entities: entitiesWithCharacter(form) ?? undefined,
    productRefKey: form.productRefKey ?? undefined,
    // W3:逐镜三态 + 参考派活(W2 编排产物;缺=老任务,ai 侧 Shot 归一兜底)。
    speechModes: form.speechModes ?? undefined,
    // 选人 → 逐镜 R2V 身份参考在提交时派生(withIdentityReferences,手选优先不叠加):
    // 此前只消费主定妆图 1 张,多图身份锁要手工挂 —— 演示片实证形态的自动化。
    references,
    // 0924 车 B:自由创作「素材」行(上传首帧/尾帧 或 参考图,互斥)按契约 §3.3 覆盖进来。
    // 不用素材 / 多镜 → 空补丁,上面的字段逐字不变。形态见 freeMaterial.ts 头注。
    ...freeMaterialRequest(form, references),
  };
}
